import { z } from 'zod';
import { lenientString, lenientUuid, lenientRequiredNode } from '../schema-extensions';
import { ValueHistoryNode } from './value-history-node-schema';

/**
 * Validation constants
 */
const PROPERTY_CONSTANTS = {
  UNIT_MAX_LENGTH: 100,
  STREET_NAME_MAX_LENGTH: 100,
  AREA_MAX_LENGTH: 100,
  TOWN_MAX_LENGTH: 100,
  COUNTY_MAX_LENGTH: 100,
  POSTCODE_MAX_LENGTH: 20,
  NOTES_MAX_LENGTH: 1000,
} as const;

/**
 * Lenient schema for deserializing PropertyNode from storage
 */
const PropertyNodeDeserializeSchema = z.object({
  id: lenientUuid(),
  unit: lenientString(PROPERTY_CONSTANTS.UNIT_MAX_LENGTH, ''),
  streetName: lenientString(PROPERTY_CONSTANTS.STREET_NAME_MAX_LENGTH, ''),
  area: lenientString(PROPERTY_CONSTANTS.AREA_MAX_LENGTH, ''),
  town: lenientString(PROPERTY_CONSTANTS.TOWN_MAX_LENGTH, ''),
  county: lenientString(PROPERTY_CONSTANTS.COUNTY_MAX_LENGTH, ''),
  postCode: lenientString(PROPERTY_CONSTANTS.POSTCODE_MAX_LENGTH, ''),
  notes: lenientString(PROPERTY_CONSTANTS.NOTES_MAX_LENGTH, ''),
  value: lenientRequiredNode(ValueHistoryNode)
}).transform(val => ({
  id: val.id,
  unit: val.unit,
  streetName: val.streetName,
  area: val.area,
  town: val.town,
  county: val.county,
  postCode: val.postCode,
  notes: val.notes,
  value: val.value
}));

/**
 * Strict schema for creating new PropertyNode
 */
const PropertyNodeCreateSchema = z.object({
  id: z.string().uuid(),
  unit: z.string().max(PROPERTY_CONSTANTS.UNIT_MAX_LENGTH),
  streetName: z.string().min(1, 'Street name is required').max(PROPERTY_CONSTANTS.STREET_NAME_MAX_LENGTH),
  area: z.string().max(PROPERTY_CONSTANTS.AREA_MAX_LENGTH),
  town: z.string().min(1, 'Town is required').max(PROPERTY_CONSTANTS.TOWN_MAX_LENGTH),
  county: z.string().max(PROPERTY_CONSTANTS.COUNTY_MAX_LENGTH),
  postCode: z.string().min(1, 'Postcode is required').max(PROPERTY_CONSTANTS.POSTCODE_MAX_LENGTH),
  notes: z.string().max(PROPERTY_CONSTANTS.NOTES_MAX_LENGTH),
  value: z.unknown().transform(val => ValueHistoryNode.create(val as Partial<ValueHistoryNode>))
}).transform(val => ({
  id: val.id,
  unit: val.unit,
  streetName: val.streetName,
  area: val.area,
  town: val.town,
  county: val.county,
  postCode: val.postCode,
  notes: val.notes,
  value: val.value
}));

/**
 * PropertyNode interface
 * Represents a real estate property
 */
export interface PropertyNode {
  id: string;
  unit: string;
  streetName: string;
  area: string;
  town: string;
  county: string;
  postCode: string;
  notes: string;
  value: ValueHistoryNode;
}

/**
 * PropertyNode namespace with factory and deserialization methods
 */
export namespace PropertyNode {
  export const Constants = PROPERTY_CONSTANTS;

  export function deserialize(data: unknown): PropertyNode {
    return PropertyNodeDeserializeSchema.parse(data);
  }

  export function deserializeSafe(data: unknown) {
    const result = PropertyNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  export function create(data: Partial<PropertyNode>): PropertyNode {
    return PropertyNodeCreateSchema.parse({
      id: data.id ?? crypto.randomUUID(),
      ...data
    });
  }

  export function validate(data: unknown): data is PropertyNode {
    return PropertyNodeDeserializeSchema.safeParse(data).success;
  }

  /**
   * Get full address as a single string
   */
  export function getFullAddress(node: PropertyNode): string {
    const parts = [
      node.unit,
      node.streetName,
      node.area,
      node.town,
      node.county,
      node.postCode
    ].filter(part => part.trim() !== '');
    
    return parts.join(', ');
  }

  /**
   * Get current property value
   */
  export function getCurrentValue(node: PropertyNode): number | undefined {
    return ValueHistoryNode.getCurrentValue(node.value);
  }

  /**
   * Check if property has been sold
   */
  export function isSold(node: PropertyNode): boolean {
    return ValueHistoryNode.isSold(node.value);
  }

  export function isActive(node: PropertyNode): boolean {
    return !PropertyNode.isSold(node);
  }
}
